import React from 'react';
import { useSelector } from 'react-redux';
import type { ImageIntentContext } from '../../../../utils/imageIntentReporter';
import { IMAGE_INTENT_ATTRIBUTE } from './ImageIntentTouchStyle';
import {
  selectImageProtectionEnabled,
  selectImageProtectionStyle,
} from '../../../../selectors/imageIntent';
import StyledOverlay from './StyledOverlay';
import useImageIntentFriction, {
  IMAGE_INTENT_SHIELD_ATTRIBUTE,
} from './useImageIntentFriction';

// 1x1 fully-transparent PNG, inlined so the decoy needs no network request
// and ships no new asset file.
const TRANSPARENT_PIXEL_SRC =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

// Sits above every other layer this file or SplitBlurOverlay draws (both cap
// out at z-index 2, and SplitBlurOverlay's internal toggle button's z-index 3
// is local to its own stacking context — from out here the whole tree paints
// as a unit at 2) as well as the zoom magnifier button that some call sites
// (e.g. PoseItem.jsx) layer over the image at z-index 2.
const SHIELD_Z_INDEX = 3;

// Every layout- and behaviour-critical property here is inline, on purpose,
// not a styled-component class. A styled-component's generated class has
// specificity (0,1,0); several hosts this overlay renders inside declare a
// plain descendant selector, `img { ... }` — e.g. PoseItem.jsx's
// `ImgContainer` sets `img { pointer-events: none; height: auto; max-width:
// 100% }` — which compiles to (0,1,1) and wins on every property it shares
// with a class-based rule. That would silently turn the shield back into a
// no-op there: not hit-testable, and collapsed to its intrinsic 1x1 size
// instead of filling the box. It's the same failure mode `SplitBlurOverlay`
// already had to defend its own z-index and line-height against (see that
// file's comments) — a host `img {}` or container rule reaching in and
// overriding one of this feature's own layers. Inline styles beat any
// non-`!important` selector regardless of specificity, which is what
// `SplitBlurOverlay` relies on for the same reason, so there is nothing left
// here for a styled-component class to usefully own.
const shieldStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  width: '100%',
  height: '100%',
  maxWidth: 'none',
  objectFit: 'fill',
  pointerEvents: 'auto',
  zIndex: SHIELD_Z_INDEX,
};

// A decoy hit target layered above the protection overlay so that a
// right-click "Save image as" or an iOS long-press save sheet targets this
// transparent pixel instead of the real photo underneath. This is defence
// that does not depend on our `contextmenu` preventDefault winning — some
// browsers and extensions let a user override that — because the browser
// picks its save target purely from what it hit-tested, before any of our
// JS runs.
//
// Being hit-testable (`pointer-events: auto`) is exactly what makes this
// work, but it's also what would otherwise swallow the ordinary click that
// selects a pose or opens a product on this file's ~24 call sites. Those
// call sites put their click handler in different places — directly on the
// `<img>`, on an ancestor `<div role="button">`, sometimes both — so rather
// than special-case each one, every click on the shield is forwarded to
// whatever is genuinely underneath it: hide the shield, ask the browser what
// element is at that point, restore the shield, then replay the click on
// that element. This also naturally forwards to interactive chrome that sits
// above the image (the zoom magnifier button, the split-blur toggle), since
// whatever is topmost-beneath still receives the forwarded click.
//
// The shield is also the one place `getFrictionProps`' `contextmenu`,
// `dragstart` and touch handlers (touchcancel included, so a late
// cancel still counts as the long-press) need to be wired directly, rather
// than relying on them bubbling up from underneath: since the shield sits
// above everything and absorbs the event, nothing below it ever sees a
// `contextmenu`/`dragstart`/`touchstart` in the first place at call
// sites that spread `getFrictionProps` straight onto a sibling `<img>` (the
// shield and that `<img>` share a parent, so events on the shield never
// bubble through it). Reporting and `contextmenu` suppression have to happen
// here, at the point where the events actually land. This incidentally makes
// telemetry *more* consistent than before this shield existed: the shield is
// now the hit target everywhere, so reporting no longer depends on whether a
// given call site happened to wire `getFrictionProps` on the `<img>` itself
// or on a shared ancestor.
//
// At call sites that DO wire `getFrictionProps` on a shared ancestor (i.e.
// everything routed through `ProtectedImage`), that ancestor still has its
// own friction handlers, and the shield's events bubble up into them. The
// shield carries IMAGE_INTENT_SHIELD_ATTRIBUTE, and those handlers skip an
// event that came from a shield inside them, which is what guarantees
// exactly one report per gesture. Do not lean on the reporter's throttle
// window instead; the throttle exists to collapse rapid repeats of the
// *same* signal, not to silently absorb a structural double report, and
// relying on it here would be a coincidence, not a guarantee.
//
// Touch events must keep bubbling: the yearbook pickers render this overlay
// straight inside ModalCarousel's slides, and the carousel's swipe
// (react-easy-swipe) listens for touchstart/touchend on an ancestor, so a
// shield that stopped them would freeze swiping at every protected studio.
// `contextmenu` and `dragstart` still stop here: the shield has already
// reported and, where it blocks, cancelled them, nothing above needs them
// (the hosts' own handlers only preventDefault, which is done), and nothing
// swipes on them.
//
// The shield carries the same `data-image-intent` attribute as the image, so
// ImageIntentTouchStyle keeps the iOS long-press sheet closed on it too where
// the studio has save blocking on (inside ProtectedImage its protected-image
// rule does so regardless); otherwise the sheet offers the transparent pixel
// rather than the photo.
//
// The context is the one the image reports with — the gallery triple and a
// pooled image's own gallery included — so the shield never changes an
// image's attribution.
//
// Deterrent only, like the rest of this file: a determined user can still
// read the real `src` straight out of the DOM. This does not, and cannot,
// prevent an image from being saved.
interface SaveShieldProps {
  context?: ImageIntentContext;
}

const SaveShield = ({ context }: SaveShieldProps) => {
  const { getFrictionProps } = useImageIntentFriction();
  const friction = getFrictionProps(context);

  const handleClick = (event: React.MouseEvent<HTMLImageElement>) => {
    // Nothing else should happen with the original event — the forwarded
    // click below is the one and only click that should reach a handler.
    event.preventDefault();
    event.stopPropagation();

    const shield = event.currentTarget;
    const {
      clientX,
      clientY,
      button,
      detail,
      ctrlKey,
      shiftKey,
      altKey,
      metaKey,
    } = event;

    // Step the shield out of the hit-testing path so elementFromPoint finds
    // whatever is genuinely underneath it, not the shield itself.
    shield.style.pointerEvents = 'none';
    const target = document.elementFromPoint(clientX, clientY);
    shield.style.pointerEvents = 'auto';

    if (target) {
      // Only `click` is forwarded here — a double-click still arrives at
      // `target` as two independent synthetic clicks (no `dblclick` is
      // synthesised), and a middle-click (`auxclick`) is absorbed by the
      // shield with nothing forwarded at all. Neither has a live consumer
      // today; this is documented scope, not an oversight, so a future call
      // site doesn't assume full mouse-event parity with a real click
      // target. The modifier keys and `button`/`detail` ARE carried through
      // below, unlike the click's own coordinates-only origin, because a
      // future handler (e.g. cmd-click-to-open) needs them to exist on the
      // forwarded event even though nothing reads them yet.
      target.dispatchEvent(
        new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          clientX,
          clientY,
          button,
          detail,
          ctrlKey,
          shiftKey,
          altKey,
          metaKey,
        }),
      );
    }
  };

  const handleContextMenu = (event: React.MouseEvent<HTMLImageElement>) => {
    friction.onContextMenu(event);
    event.stopPropagation();
  };

  const handleDragStart = (event: React.DragEvent<HTMLImageElement>) => {
    friction.onDragStart(event);
    event.stopPropagation();
  };

  return (
    <img
      data-testid="image-protection-save-shield"
      {...{ [IMAGE_INTENT_ATTRIBUTE]: friction[IMAGE_INTENT_ATTRIBUTE] }}
      {...{ [IMAGE_INTENT_SHIELD_ATTRIBUTE]: '' }}
      src={TRANSPARENT_PIXEL_SRC}
      alt=""
      aria-hidden="true"
      tabIndex={-1}
      // Draggable on purpose: a mouse or trackpad drag over the photo lands
      // here, and only a draggable element fires dragstart. The handler
      // reports the attempt and cancels the drag (protection implies save
      // blocking), so the pixel never travels. `draggable={false}` would make
      // protected drags vanish from the telemetry instead of being blocked.
      draggable
      style={shieldStyle}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      onDragStart={handleDragStart}
      onTouchStart={friction.onTouchStart}
      onTouchMove={friction.onTouchMove}
      onTouchEnd={friction.onTouchEnd}
      onTouchCancel={friction.onTouchCancel}
    />
  );
};

interface ImageProtectionOverlayProps {
  context?: ImageIntentContext;
  imageSrc?: string;
}

// Renders the studio's chosen protection treatment (StyledOverlay, the one
// render switch over the style registry) inside any `position: relative`
// container when the studio has image protection enabled. Pass the same
// context the image reports with.
//
// Cmd/Ctrl+S is not registered here: every protected surface registers it
// through useImageIntentFriction, which asks to suppress it under protection.
// A per-tile registration would win the shortcut's attribution (the most
// recent registration does), crediting a page save to one image's gallery on
// screens that list several — the cart and the dashboard credit none.
const ImageProtectionOverlay = ({
  context = {},
  imageSrc,
}: ImageProtectionOverlayProps) => {
  const protectionEnabled = useSelector(selectImageProtectionEnabled);
  const protectionStyle = useSelector(selectImageProtectionStyle);

  if (!protectionEnabled) {
    return null;
  }

  // The shield is gated on the master flag only, independent of which
  // treatment above is selected — every style gets the same save-target
  // decoy.
  return (
    <>
      <StyledOverlay style={protectionStyle} imageSrc={imageSrc} />
      <SaveShield context={context} />
    </>
  );
};

export default ImageProtectionOverlay;
